import {
  BadgeDollarSign,
  CircleDollarSign,
  ClipboardList,
  MessageCircleMore,
  Package,
  Bath,
  UserRoundCheck,
} from "lucide-react";

import { Pagination } from "../../../../components/ui/Pagination";
import { Summary } from "../../../../components/ui/Summary";
import { Table } from "../../../../components/ui/Table";
import { CrmPetDiagnosisController } from "../../../../services/controllers/crmPetDiagnosis";
import { useCrmPetDiagnosis } from "../../../../store/crmPetDiagnosis/useCrmPetDiagnosis";
import { EmptyState } from "../components/EmptyState";
import { MetricCard } from "../components/MetricCard";
import { ResultyRow } from "./ResultyRow";
import { SectionCard } from "../components/SectionCard";
import { SectionError } from "../components/SectionError";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  safeDivide,
} from "../helpers";
import { ResultsFilter } from "./ResultsFilter";

export function Results() {
  const controller = CrmPetDiagnosisController();
  const attempts = useCrmPetDiagnosis((state) => state.data.attempts);
  const pagination = useCrmPetDiagnosis((state) => state.data.attemptsPagination);
  const kpis = useCrmPetDiagnosis((state) => state.data.resultsKpis);
  const contactedKpis = useCrmPetDiagnosis((state) => state.data.resultsContactedKpis);
  const loading = useCrmPetDiagnosis((state) => state.data.loading);
  const error = useCrmPetDiagnosis((state) => state.data.errors);

  const summaryMetrics = [
    {
      id: "crm-results-total-customers",
      icon: UserRoundCheck,
      value: formatNumber(contactedKpis?.attempts),
      label: "Total de clientes",
      loading: loading.resultsContactedKpis,
    },
    {
      id: "crm-results-total-contacts",
      icon: MessageCircleMore,
      value: formatNumber(kpis?.contactsMade),
      label: "Total de contatos",
      loading: loading.resultsKpis,
    },
    {
      id: "crm-results-customers-with-revenue",
      icon: BadgeDollarSign,
      value: formatNumber(kpis?.customersWithRevenue),
      label: "Clientes que gastaram",
      loading: loading.resultsKpis,
    },
    {
      id: "crm-results-service-revenue",
      icon: Bath,
      value: formatCurrency(kpis?.serviceRevenue),
      label: "Valor total de serviço",
      loading: loading.resultsKpis,
    },
    {
      id: "crm-results-product-revenue",
      icon: Package,
      value: formatCurrency(kpis?.productRevenue),
      label: "Valor total de produto",
      loading: loading.resultsKpis,
    },
    {
      id: "crm-results-total-revenue",
      icon: CircleDollarSign,
      value: formatCurrency(kpis?.totalRevenue),
      label: "Receita atribuída",
      loading: loading.resultsKpis,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <ResultsFilter />
      <Summary.Root className="!grid !gap-4 !grid-cols-3">
        {summaryMetrics
          .filter((metric) => {
            if (metric.id === "crm-results-total-customers") {
              return !error.resultsContactedKpis;
            }
            return !error.resultsKpis;
          })
          .map((metric) => (
            <MetricCard key={metric.id} {...metric} className="!min-w-0" />
          ))}
      </Summary.Root>

      {error.attempts && (
        <SectionError message={error.attempts} onRetry={() => controller.retrySection("attempts")} />
      )}
      {!error.attempts && (
        <Table.Root name={controller.attemptsTableName} config>
          <Table.Header
            columns={[
              "Ausência:100px",
              "Cliente / Pet:minmax(190px, 1.4fr)",
              "Contatos:100px",
              "Primeiro contato:150px:true",
              "Último contato:150px:true",
              "Status:200px",
              "Serviços:120px",
              "Produtos:120px",
              "Total atribuído:minmax(150px, 1fr)",
            ]}
          />
          <Table.Content isLoading={loading.attempts} loadingLine={8}>
            {!loading.attempts && !attempts.length && (
              <EmptyState
                title="Nenhum cliente contatado no período"
                description="A tabela usa contacted=true e inclui recuperações que avançaram de status."
              />
            )}
            {attempts.map((attempt) => (
              <ResultyRow key={attempt._id} attempt={attempt} />
            ))}
          </Table.Content>
          <Pagination.Root
            name={controller.attemptsPaginationName}
            totalPages={Math.max(0, pagination.totalPages - 1)}
            currentPage={pagination.page}
            onChange={controller.setAttemptsPage}
          >
            <Pagination.Back />
            <Pagination.Pages />
            <Pagination.Forward />
          </Pagination.Root>
        </Table.Root>
      )}
    </div >
  );
}
